import React from 'react';

import {
  getDefaultConsent,
  getLocalConsent,
  getRemoteConsent,
  getShouldNotTrack
} from 'helpers/ConsentHelpers';
import { isAutomationTest } from 'helpers/utils';
import { useAccountMutation } from 'hooks/data/useAccountMutation';
import { useConsent } from 'hooks/data/useConsent';
import { useConsentCookieDefinitions } from 'hooks/data/useConsentCookieDefinitions';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';

export function useConsentStatus() {

  // Hooks

  // --- Data

  const { isSettled: isAuthenticationSettled, isAuthenticated } = useIsAuthenticated();

  const { isStale: isConsentStale, data: userConsent } = useConsent({ enabled: isAuthenticated });
  const { isLoading: isLoadingCookieDefinitions, data: consentCookieDefinitions = [] } = useConsentCookieDefinitions();

  // --- Mutation

  const { isLoading: isMutationLoading, updateConsent } = useAccountMutation();

  // Props

  const shouldNotTrack = getShouldNotTrack();

  const defaultConsent = React.useMemo(() => getDefaultConsent(consentCookieDefinitions), [consentCookieDefinitions]);
  const localConsent = React.useMemo(() => getLocalConsent(consentCookieDefinitions), [consentCookieDefinitions]);
  const remoteConsent = React.useMemo(() => getRemoteConsent(userConsent, consentCookieDefinitions), [userConsent, consentCookieDefinitions]);

  const {
    hasGivenConsent: hasGivenLocalConsent,
    shouldRequestConsent: shouldRequestLocalConsent,
  } = localConsent || {};

  const {
    hasGivenConsent: hasGivenRemoteConsent,
    shouldRequestConsent: shouldRequestRemoteConsent,
  } = remoteConsent || {};

  // Flags

  const isEnabled = (
    isAuthenticationSettled
    && (!isAuthenticated || (isAuthenticated && !!userConsent))
    && !isAutomationTest()
  );

  const isLoading = (
    isLoadingCookieDefinitions
    || isConsentStale
    || isMutationLoading
  );

  // --- Should store consent locally if unauthenticated

  const shouldStoreConsentLocally = (
    isAuthenticationSettled
    && !isAuthenticated
    && !userConsent
  );

  // --- Should sync local consent with remote consent

  const shouldSyncConsent = (
    isEnabled
    && !isLoading
    && !shouldNotTrack
    && !shouldStoreConsentLocally
    && !hasGivenRemoteConsent
    && hasGivenLocalConsent
  );

  // --- Should request consent

  const shouldRequestConsent = (
    (shouldStoreConsentLocally && shouldRequestLocalConsent)
    || (shouldRequestRemoteConsent && !isLoading && !shouldSyncConsent)
  );

  // --- Should show consent banner

  const shouldShowConsentBanner = (
    isEnabled
    && !shouldNotTrack
    && shouldRequestConsent
  );

  // ------ Should set cookie policy on window variable

  const hasGivenConsent = (
    shouldStoreConsentLocally
      ? hasGivenLocalConsent
      : hasGivenRemoteConsent
  );

  const shouldSetCookiePolicy = (
    isEnabled
    && !isLoading
    && (shouldNotTrack || (!shouldSyncConsent && hasGivenConsent))
  );

  // --- Cookie Policy

  const cookiePolicy = (
    shouldNotTrack
      ? defaultConsent
      : shouldStoreConsentLocally
        ? localConsent
        : remoteConsent
  );

  // Return

  return {
    //
    shouldStoreConsentLocally,
    shouldSyncConsent,
    shouldShowConsentBanner,
    shouldSetCookiePolicy,
    //
    defaultConsent,
    localConsent,
    remoteConsent,
    cookiePolicy,
    //
    updateRemoteConsent: updateConsent,
    //
  };
}
